import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { getFunctions, httpsCallable } from "firebase/functions";
import { format } from "date-fns";

const PRIORITY_CHIP = {
  low: { color: "success", label: "Low" },
  medium: { color: "warning", label: "Medium" },
  high: { color: "error", label: "High" },
  urgent: { color: "error", label: "Urgent" },
};

const isImageFile = (fileName) => {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext);
};

export default function PublicIssueView() {
  const { issueId } = useParams();
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property");
  const clientId = searchParams.get("clientId");

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!propertyId || !issueId) {
      setError("Invalid issue link — missing property or issue ID.");
      setLoading(false);
      return;
    }

    const functions = getFunctions();
    const getPublicIssue = httpsCallable(functions, "getPublicIssue");

    getPublicIssue({ clientId, propertyId, issueId })
      .then((result) => setIssue(result.data))
      .catch(() => setError("Failed to load issue. The link may be invalid."))
      .finally(() => setLoading(false));
  }, [clientId, propertyId, issueId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const p = PRIORITY_CHIP[issue.priority] || PRIORITY_CHIP.medium;
  const submittedDate = issue.submittedAt
    ? format(new Date(issue.submittedAt), "MMMM do, yyyy h:mm a")
    : null;

  const quotes = (issue.files || []).filter((f) => f.isQuote);
  const otherFiles = (issue.files || []).filter((f) => !f.isQuote);
  const imageFiles = otherFiles.filter((f) => isImageFile(f.name));
  const docFiles = otherFiles.filter((f) => !isImageFile(f.name));

  return (
    <Box maxWidth={800} mx="auto" p={3}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#282828",
          borderRadius: 2,
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h5" color="white" fontWeight={600}>
            Issue Report
          </Typography>
          <Typography variant="body2" color="grey.400" mt={0.5}>
            Issue #{issue.id.slice(0, 6)}
          </Typography>
        </Box>
        <Chip color={p.color} label={p.label} />
      </Box>

      {/* Meta */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>
            {issue.status || "Not Reported"}
          </Typography>
        </Box>
        {issue.assignedTo && (
          <Box>
            <Typography variant="caption" color="text.secondary">Assigned To</Typography>
            <Typography variant="body2" fontWeight={500}>{issue.assignedTo}</Typography>
          </Box>
        )}
        {issue.vendor && issue.vendor !== "unknown" && (
          <Box>
            <Typography variant="caption" color="text.secondary">Vendor</Typography>
            <Typography variant="body2" fontWeight={500}>{issue.vendor}</Typography>
          </Box>
        )}
        {submittedDate && (
          <Box>
            <Typography variant="caption" color="text.secondary">Submitted</Typography>
            <Typography variant="body2" fontWeight={500}>{submittedDate}</Typography>
          </Box>
        )}
      </Box>

      {/* Description */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Description
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {issue.issueDetails || "No description provided."}
        </Typography>
      </Box>

      {/* Files */}
      {(quotes.length > 0 || imageFiles.length > 0 || docFiles.length > 0) && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Attachments ({(issue.files || []).length})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {quotes.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" fontWeight={600} mb={1}>
                Quotes
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {quotes.map((f, i) => (
                  <Tooltip key={i} title={f.name || `Quote ${i + 1}`} arrow>
                    <Card
                      onClick={() => window.open(f.url, "_blank", "noopener,noreferrer")}
                      sx={{ width: 100, cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          bgcolor: "primary.light",
                        }}
                      >
                        <PictureAsPdfIcon sx={{ fontSize: 36, color: "primary.dark" }} />
                        <Typography variant="caption" fontWeight={600} color="primary.dark" mt={0.5}>
                          Quote {i + 1}
                        </Typography>
                      </Box>
                    </Card>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {imageFiles.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" fontWeight={600} mb={1}>
                Images
              </Typography>
              <Grid container spacing={1}>
                {imageFiles.map((f, i) => (
                  <Grid item key={i}>
                    <Tooltip title={f.name || `Image ${i + 1}`} arrow>
                      <Card
                        onClick={() => window.open(f.url, "_blank", "noopener,noreferrer")}
                        sx={{ width: 120, height: 120, cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={f.url}
                          alt={f.name}
                          sx={{ objectFit: "cover" }}
                        />
                      </Card>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {docFiles.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight={600} mb={1}>
                Documents
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {docFiles.map((f, i) => (
                  <Tooltip key={i} title={f.name || `File ${i + 1}`} arrow>
                    <Card
                      onClick={() => window.open(f.url, "_blank", "noopener,noreferrer")}
                      sx={{ width: 100, cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          bgcolor: "grey.100",
                        }}
                      >
                        <InsertDriveFileIcon sx={{ fontSize: 36, color: "primary.main" }} />
                        <Typography
                          variant="caption"
                          mt={0.5}
                          sx={{
                            textAlign: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {f.name || `File ${i + 1}`}
                        </Typography>
                      </Box>
                    </Card>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box mt={4} textAlign="center">
        <Typography variant="caption" color="text.secondary">
          Shared via RoomRoster
        </Typography>
      </Box>
    </Box>
  );
}
